import React, { createContext, useContext, useState, useEffect, useCallback } from 'react';
import { authService, userService } from '../services';

const AuthContext = createContext();

export const AuthProvider = ({ children }) => {
  const [user, setUser] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  // Initialize auth state from localStorage
  useEffect(() => {
    const initAuth = async () => {
      const token = localStorage.getItem('accessToken');
      const storedUser = localStorage.getItem('user');

      if (token && storedUser) {
        try {
          // Verify token is still valid by fetching current user
          // API returns { success, data: { user } }
          const response = await authService.getMe();
          const userData = response.data?.user;
          setUser(userData);
          localStorage.setItem('user', JSON.stringify(userData));
        } catch (err) {
          // Token invalid, clear auth state
          localStorage.removeItem('accessToken');
          localStorage.removeItem('refreshToken');
          localStorage.removeItem('user');
          setUser(null);
        }
      }
      setLoading(false);
    };

    initAuth();
  }, []);

  const login = useCallback(async (email, password) => {
    setError(null);
    setLoading(true);
    try {
      const response = await authService.login({ email, password });
      // API returns { success, data: { user, tokens: { accessToken, refreshToken } } }
      const userData = response.data?.user;
      const { accessToken, refreshToken } = response.data?.tokens || {};

      if (!accessToken || !refreshToken) {
        throw new Error('Invalid response from server');
      }

      localStorage.setItem('accessToken', accessToken);
      localStorage.setItem('refreshToken', refreshToken);
      localStorage.setItem('user', JSON.stringify(userData));

      setUser(userData);
      setLoading(false);
      return { success: true, user: userData };
    } catch (err) {
      const message = err.response?.data?.error?.message || err.response?.data?.message || err.message || 'Login failed';
      setError(message);
      setLoading(false);
      return { success: false, error: message };
    }
  }, []);

  const register = useCallback(async (email, password, displayName) => {
    setError(null);
    setLoading(true);
    try {
      const response = await authService.register({ email, password, displayName });
      // API returns { success, data: { user, tokens: { accessToken, refreshToken } } }
      const userData = response.data?.user;
      const { accessToken, refreshToken } = response.data?.tokens || {};

      if (!accessToken || !refreshToken) {
        throw new Error('Invalid response from server');
      }

      localStorage.setItem('accessToken', accessToken);
      localStorage.setItem('refreshToken', refreshToken);
      localStorage.setItem('user', JSON.stringify(userData));

      setUser(userData);
      setLoading(false);
      return { success: true, user: userData };
    } catch (err) {
      const message = err.response?.data?.error?.message || err.response?.data?.message || err.message || 'Registration failed';
      setError(message);
      setLoading(false);
      return { success: false, error: message };
    }
  }, []);

  // Google sign-in / sign-up. Pass the ID token returned by Google
  // Identity Services; the API decides whether this is a new user or
  // an existing one and we treat both the same on the client. Same
  // localStorage shape and return contract as login()/register() so
  // any caller can use this interchangeably.
  const googleSignIn = useCallback(async (idToken) => {
    setError(null);
    setLoading(true);
    try {
      const response = await authService.googleSignIn(idToken);
      const userData = response.data?.user;
      const { accessToken, refreshToken } = response.data?.tokens || {};
      const isNewUser = !!response.data?.isNewUser;

      if (!accessToken || !refreshToken) {
        throw new Error('Invalid response from server');
      }
      localStorage.setItem('accessToken', accessToken);
      localStorage.setItem('refreshToken', refreshToken);
      localStorage.setItem('user', JSON.stringify(userData));

      setUser(userData);
      setLoading(false);
      return { success: true, user: userData, isNewUser };
    } catch (err) {
      const message = err.response?.data?.error?.message || err.response?.data?.message || err.message || 'Google sign-in failed';
      setError(message);
      setLoading(false);
      return { success: false, error: message };
    }
  }, []);

  const logout = useCallback(() => {
    localStorage.removeItem('accessToken');
    localStorage.removeItem('refreshToken');
    localStorage.removeItem('user');
    setUser(null);
    setError(null);
  }, []);

  const refreshUser = useCallback(async () => {
    try {
      // API returns { success, data: { user } }
      const response = await authService.getMe();
      const userData = response.data?.user;
      setUser(userData);
      localStorage.setItem('user', JSON.stringify(userData));
      return userData;
    } catch (err) {
      return null;
    }
  }, []);

  const updateCredits = useCallback(async () => {
    try {
      const response = await userService.getCredits();
      if (user) {
        const updatedUser = { ...user, creditBalance: response.data.creditBalance };
        setUser(updatedUser);
        localStorage.setItem('user', JSON.stringify(updatedUser));
      }
      return response.data;
    } catch (err) {
      return null;
    }
  }, [user]);

  const clearError = useCallback(() => {
    setError(null);
  }, []);

  const value = {
    user,
    loading,
    error,
    isAuthenticated: !!user,
    login,
    register,
    googleSignIn,
    logout,
    refreshUser,
    updateCredits,
    clearError,
  };

  return (
    <AuthContext.Provider value={value}>
      {children}
    </AuthContext.Provider>
  );
};

export const useAuth = () => {
  const context = useContext(AuthContext);
  if (!context) {
    throw new Error('useAuth must be used within an AuthProvider');
  }
  return context;
};

export default AuthContext;
